import { useApolloClient } from '@apollo/client/react'
import React from 'react'


import { OnChangeCallback, Suggestion } from '../../../components/layout/form/field/CreatableAutosuggestField'
import { CreatableAutosuggestInput } from '../../../components/layout/form/input/CreatableAutosuggestInput'
import { CustomersOutput, query } from './CustomerAutoSuggestQuery'
import { Customer } from './types'

interface Props {
  name: string
  label?: string
  required?: boolean
  searchType: SearchType
  onChange: OnChangeCallback<Customer>
  placeholder?: string
  disabled?: boolean
}

type SearchType = 'company' | 'person'

const handleSearch = async (
  client: ReturnType<typeof useApolloClient>,
  searchString: string,
  searchType: SearchType
): Promise<Suggestion[]> => {
  if (searchString.length > 1) {
    const { data } = await client.query<{ customers: CustomersOutput }>({
      query,
      variables: { searchString, type: searchType },
      fetchPolicy: 'network-only',
    })
    if (!data) return []
    return onCustomerFetched(data.customers)
  }
  return []
}

const onCustomerFetched = (customersOutput: CustomersOutput): Suggestion[] => {
  if (customersOutput.__typename === 'GetCustomersSuccess') {
    return customersOutput.customers.map(customer => {
      if (customer.__typename === 'CompanyCustomer') {
        return { label: `${customer.companyName} / ${customer.contactPerson}`, value: customer.id, object: customer }
      }
      if (customer.__typename === 'PersonCustomer') {
        return { label: `${customer.firstName} ${customer.lastName}`, value: customer.id, object: customer }
      }
      return { label: 'Undefined', value: 0 }
    })
  }
  return []
}

export const CustomerAutoSuggest: React.FunctionComponent<Props> = ({
  name,
  label,
  searchType,
  onChange,
  placeholder,
  disabled,
  required,
}) => {
  const client = useApolloClient()

  return (
    <CreatableAutosuggestInput
      disabled={disabled}
      name={name}
      label={label}
      getOptions={async searchString => {
        return await handleSearch(client, searchString, searchType)
      }}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
    />
  )
}
